import { z } from 'zod';

export const NormalizedDateSchema = z.looseObject({
  original: z.string().optional(),
  normalized: z.string().optional(), // often ISO date, but keep loose
});

export const NormalizedStatusSchema = z.looseObject({
  original: z.string().optional(),
  normalized: z.enum(['Active', 'Inactive', 'Distressed', 'Other']).optional(),
});

export const NormalizedLegalFormSchema = z.looseObject({
  original: z.string().optional(),
  normalized: z.string().optional(),
});

export const RoleSchema = z.looseObject({
  original: z.string().optional(),
  normalized: z.string().optional(),
});

/**
 * Shared models
 */
export const AddressSchema = z.looseObject({
  type: z.string().optional(),
  fullAddress: z.string().optional(),
  rawAddressLines: z.array(z.string()).optional(),
  buildingName: z.string().optional(),
  streetNumber: z.string().optional(),
  streetName: z.string().optional(),
  city: z.string().optional(),
  postcode: z.string().optional(),
  municipality: z.string().optional(),
  region: z.string().optional(),
  country: z.string().optional(),
  isoCode: z.string().optional(),
  secondaryPostalCode: z.string().optional(),
});

export const ContactDetailsSchema = z.looseObject({
  email: z.string().optional(),
  fax: z.string().optional(),
  telNumber: z.string().optional(),
  website: z.string().optional(),
});

export const CompanyLinksSchema = z.looseObject({
  document: z.string().optional(),
  data: z.string().optional(),
});

export const SourceLinkSchema = z.looseObject({
  label: z.string().optional(),
  url: z.string().optional(),
});

/**
 * Identifiers
 */
export const IdentifierSchema = z.looseObject({
  value: z.string().optional(),
  type: z.string().optional(),
});

export const IdentifiersSchema = z.looseObject({
  primaryRegistrationNumber: z.string().optional(),
  taxNumber: z.string().optional(), // deprecated in schema text, but keep
  secondaryRegistrationNumber: z.string().optional(), // deprecated in schema text, but keep
  otherIdentifiers: z.array(IdentifierSchema).optional(),
});

/**
 * Activities
 */
export const ActivitySchema = z.looseObject({
  code: z.string().optional(),
  description: z.string().optional(),
  classificationScheme: z.enum(['NACE', 'SIC', 'SIC07', 'NAICS']).optional(),
  type: z.enum(['Primary', 'Secondary']).optional(),
});

export const ActivityDeclarationSchema = z.looseObject({
  declaration: z.string().optional(),
  declarationDescription: z.string().optional(),
  language: z.string().optional(),
});

/**
 * People / corporations (used in shareholders, representatives, UBOs)
 */
export const PersonDetailsSchema = z.looseObject({
  type: z.literal('Person'),
  idNumber: z.string().optional(),
  name: z.string().optional(),
  address: AddressSchema.optional(),
  birthdate: NormalizedDateSchema.optional(),
  nationality: z.string().optional(),
  placeOfBirth: AddressSchema.optional(),
  placeOfResidence: AddressSchema.optional(),
});

export const CorporationDetailsSchema = z.looseObject({
  type: z.literal('Corporation'),
  idNumber: z.string().optional(),
  name: z.string().optional(),
  address: AddressSchema.optional(),
  registrationNumber: z.string().optional(),
  registrationDate: NormalizedDateSchema.optional(),
  registrationAuthority: z.string().optional(),
  registeredAddress: AddressSchema.optional(),
});

export const LegalEntitySchema = z
  .discriminatedUnion('type', [PersonDetailsSchema, CorporationDetailsSchema])
  .or(z.looseObject({ type: z.string().optional() })); // future/other variants (Other, etc.)

/**
 * Shareholding / capital
 */
export const ShareholdersSchema = z.looseObject({
  corporations: z.array(CorporationDetailsSchema).optional(),
  individuals: z.array(PersonDetailsSchema).optional(),
});

export const ShareholdingSchema = z.looseObject({
  percentage: z.string().optional(),
  count: z.number().optional(),
  totalNominalValue: z.number().optional(),
  currency: z.string().optional(), // deprecated in schema text, but keep
  beneficiallyHeld: z.boolean().optional(),
  shareholders: ShareholdersSchema.optional(),
});

export const CapitalSchema = z.looseObject({
  type: z.string().optional(), // deprecated in schema text
  description: z.string().optional(), // deprecated in schema text
  classCode: z.string().optional(),
  classDescription: z.string().optional(),
  quantity: z.number().optional(),
  unitNominalValue: z.number().optional(),
  totalNominalValue: z.number().optional(),
  currency: z.string().optional(),
  votingRights: z.string().optional(),
  shareholdings: z.array(ShareholdingSchema).optional(),
});

export const TotalCapitalSchema = z.looseObject({
  amount: z.number().optional(),
  currency: z.string().optional(),
  type: z.enum(['Fixed', 'Variable']).optional(),
});

/**
 * Representatives
 */
export const DirectorshipSchema = z.looseObject({
  companyNumber: z.string().optional(),
  companyName: z.string().optional(),
  role: z.string().optional(),
  startDate: NormalizedDateSchema.optional(),
  endDate: NormalizedDateSchema.optional(),
  isActive: z.boolean().optional(),
});

export const PersonRepresentativeSchema = PersonDetailsSchema.extend({
  role: RoleSchema.optional(),
  startDate: NormalizedDateSchema.optional(),
  endDate: NormalizedDateSchema.optional(),
  isActive: z.boolean().optional(),
  directorships: z.array(DirectorshipSchema).optional(),
  powers: z.array(z.string()).optional(),
});

export const CorporationRepresentativeSchema = CorporationDetailsSchema.extend({
  natureOfControl: z.array(z.string()).optional(),
  role: RoleSchema.optional(),
  startDate: NormalizedDateSchema.optional(),
  endDate: NormalizedDateSchema.optional(),
  isActive: z.boolean().optional(),
  directorships: z.array(DirectorshipSchema).optional(),
  powers: z.array(z.string()).optional(),
});

export const RepresentativesSchema = z.looseObject({
  corporations: z.array(CorporationRepresentativeSchema).optional(),
  individuals: z.array(PersonRepresentativeSchema).optional(),
});

/**
 * UBOs
 */
export const IndividualUBOSchema = PersonDetailsSchema.extend({
  natureOfControl: z.array(z.string()).optional(),
  startDate: NormalizedDateSchema.optional(),
  notifiedDate: NormalizedDateSchema.optional(),
  endDate: NormalizedDateSchema.optional(),
  kind: z.string().optional(), // [FUTURE]
});

export const CorporationUBOSchema = CorporationDetailsSchema.extend({
  natureOfControl: z.array(z.string()).optional(),
  startDate: NormalizedDateSchema.optional(),
  notifiedDate: NormalizedDateSchema.optional(),
  endDate: NormalizedDateSchema.optional(),
  kind: z.string().optional(), // [FUTURE]
});

export const UltimateBeneficialOwnersSchema = z.looseObject({
  individuals: z.array(IndividualUBOSchema).optional(),
  corporations: z.array(CorporationUBOSchema).optional(),
});

/**
 * Company Profile (data)
 */
export const AlternativeNameSchema = z.looseObject({
  name: z.string().optional(),
  type: z.string().optional(),
});

export const PreviousNameSchema = z.looseObject({
  name: z.string().optional(),
  startDate: NormalizedDateSchema.optional(),
  endDate: NormalizedDateSchema.optional(),
});

export const IncorporationJurisdictionSchema = z.looseObject({
  original: z.string().optional(),
});

/**
 * TypeScript types for KyckrCompanyProfile
 * Fully typed manually to avoid LSP/TypeScript complexity issues
 */
type NormalizedDate = {
  original?: string;
  normalized?: string;
};

type NormalizedStatus = {
  original?: string;
  normalized?: 'Active' | 'Inactive' | 'Distressed' | 'Other';
};

type NormalizedLegalForm = {
  original?: string;
  normalized?: string;
};

type Role = {
  original?: string;
  normalized?: string;
};

type Address = {
  type?: string;
  fullAddress?: string;
  rawAddressLines?: string[];
  buildingName?: string;
  streetNumber?: string;
  streetName?: string;
  city?: string;
  postcode?: string;
  municipality?: string;
  region?: string;
  country?: string;
  isoCode?: string;
  secondaryPostalCode?: string;
};

type ContactDetails = {
  email?: string;
  fax?: string;
  telNumber?: string;
  website?: string;
};

type CompanyLinks = {
  document?: string;
  data?: string;
};

type SourceLink = {
  label?: string;
  url?: string;
};

type Identifier = {
  value?: string;
  type?: string;
};

type Identifiers = {
  primaryRegistrationNumber?: string;
  taxNumber?: string;
  secondaryRegistrationNumber?: string;
  otherIdentifiers?: Identifier[];
};

type Activity = {
  code?: string;
  description?: string;
  classificationScheme?: 'NACE' | 'SIC' | 'SIC07' | 'NAICS';
  type?: 'Primary' | 'Secondary';
};

type ActivityDeclaration = {
  declaration?: string;
  declarationDescription?: string;
  language?: string;
};

type Directorship = {
  companyNumber?: string;
  companyName?: string;
  role?: string;
  startDate?: NormalizedDate;
  endDate?: NormalizedDate;
  isActive?: boolean;
};

type PersonRepresentative = {
  type: 'Person';
  idNumber?: string;
  name?: string;
  address?: Address;
  birthdate?: NormalizedDate;
  nationality?: string;
  placeOfBirth?: Address;
  placeOfResidence?: Address;
  role?: Role;
  startDate?: NormalizedDate;
  endDate?: NormalizedDate;
  isActive?: boolean;
  directorships?: Directorship[];
  powers?: string[];
};

type CorporationRepresentative = {
  type: 'Corporation';
  idNumber?: string;
  name?: string;
  address?: Address;
  registrationNumber?: string;
  registrationDate?: NormalizedDate;
  registrationAuthority?: string;
  registeredAddress?: Address;
  natureOfControl?: string[];
  role?: Role;
  startDate?: NormalizedDate;
  endDate?: NormalizedDate;
  isActive?: boolean;
  directorships?: Directorship[];
  powers?: string[];
};

type Representatives = {
  corporations?: CorporationRepresentative[];
  individuals?: PersonRepresentative[];
};

type IndividualUBO = {
  type: 'Person';
  idNumber?: string;
  name?: string;
  address?: Address;
  birthdate?: NormalizedDate;
  nationality?: string;
  placeOfBirth?: Address;
  placeOfResidence?: Address;
  natureOfControl?: string[];
  startDate?: NormalizedDate;
  notifiedDate?: NormalizedDate;
  endDate?: NormalizedDate;
  kind?: string;
};

type CorporationUBO = {
  type: 'Corporation';
  idNumber?: string;
  name?: string;
  address?: Address;
  registrationNumber?: string;
  registrationDate?: NormalizedDate;
  registrationAuthority?: string;
  registeredAddress?: Address;
  natureOfControl?: string[];
  startDate?: NormalizedDate;
  notifiedDate?: NormalizedDate;
  endDate?: NormalizedDate;
  kind?: string;
};

type UltimateBeneficialOwners = {
  individuals?: IndividualUBO[];
  corporations?: CorporationUBO[];
};

type AlternativeName = {
  name?: string;
  type?: string;
};

type PreviousName = {
  name?: string;
  startDate?: NormalizedDate;
  endDate?: NormalizedDate;
};

type IncorporationJurisdiction = {
  original?: string;
};

type TotalCapital = {
  amount?: number;
  currency?: string;
  type?: 'Fixed' | 'Variable';
};

type Capital = {
  type?: string;
  description?: string;
  classCode?: string;
  classDescription?: string;
  quantity?: number;
  unitNominalValue?: number;
  totalNominalValue?: number;
  currency?: string;
  votingRights?: string;
  shareholdings?: Shareholding[];
};

type Shareholding = {
  percentage?: string;
  count?: number;
  totalNominalValue?: number;
  currency?: string;
  beneficiallyHeld?: boolean;
  shareholders?: Shareholders;
};

type Shareholders = {
  corporations?: CorporationDetails[];
  individuals?: PersonDetails[];
};

type CorporationDetails = {
  type: 'Corporation';
  idNumber?: string;
  name?: string;
  address?: Address;
  registrationNumber?: string;
  registrationDate?: NormalizedDate;
  registrationAuthority?: string;
  registeredAddress?: Address;
};

type PersonDetails = {
  type: 'Person';
  idNumber?: string;
  name?: string;
  address?: Address;
  birthdate?: NormalizedDate;
  nationality?: string;
  placeOfBirth?: Address;
  placeOfResidence?: Address;
};

export type KyckrCompanyProfile = {
  activities?: Activity[];
  activityDeclarations?: ActivityDeclaration[];
  addresses?: Address[];

  totalCapital?: TotalCapital;
  capital?: Capital[];

  representatives?: Representatives;
  ultimateBeneficialOwners?: UltimateBeneficialOwners;

  contactDetails?: ContactDetails;
  links?: CompanyLinks;
  /** Provenance links for the record (labeled, e.g. registry pages, filings). */
  sourceLinks?: SourceLink[];
  /** Clickable URL for the registrationAuthority ("Source:") header. */
  registryUrl?: string;
  identifiers?: Identifiers;

  companyName?: string;
  tradingName?: string;
  englishName?: string;
  aliases?: AlternativeName[];
  previousNames?: PreviousName[];

  registrationAuthority?: string;
  lastUpdated?: string;
  lastAnnualAccountDate?: NormalizedDate;

  foundationDate?: NormalizedDate;
  registrationDate?: NormalizedDate;
  incorporationDate?: NormalizedDate;
  dissolutionDate?: NormalizedDate;

  status?: NormalizedStatus;
  legalForm?: NormalizedLegalForm;

  incorporationJurisdiction?: IncorporationJurisdiction;
  registeredAgentName?: string;
  registeredAgentAddress?: string;
};

export const KyckrCompanyProfileSchema: z.ZodSchema<KyckrCompanyProfile> = z.looseObject({
  activities: z.array(ActivitySchema).optional(),
  activityDeclarations: z.array(ActivityDeclarationSchema).optional(),
  addresses: z.array(AddressSchema).optional(),

  totalCapital: TotalCapitalSchema.optional(),
  capital: z.array(CapitalSchema).optional(),

  representatives: RepresentativesSchema.optional(),
  ultimateBeneficialOwners: UltimateBeneficialOwnersSchema.optional(),

  contactDetails: ContactDetailsSchema.optional(),
  links: CompanyLinksSchema.optional(),
  sourceLinks: z.array(SourceLinkSchema).optional(),
  registryUrl: z.string().optional(),
  identifiers: IdentifiersSchema.optional(),

  companyName: z.string().optional(),
  tradingName: z.string().optional(),
  englishName: z.string().optional(),
  aliases: z.array(AlternativeNameSchema).optional(),
  previousNames: z.array(PreviousNameSchema).optional(),

  registrationAuthority: z.string().optional(),
  lastUpdated: z.string().optional(),

  lastAnnualAccountDate: NormalizedDateSchema.optional(),

  foundationDate: NormalizedDateSchema.optional(),
  registrationDate: NormalizedDateSchema.optional(),
  incorporationDate: NormalizedDateSchema.optional(),
  dissolutionDate: NormalizedDateSchema.optional(),

  status: NormalizedStatusSchema.optional(),
  legalForm: NormalizedLegalFormSchema.optional(),

  incorporationJurisdiction: IncorporationJurisdictionSchema.optional(),
  registeredAgentName: z.string().optional(),
  registeredAgentAddress: z.string().optional(),
});

/**
 * Order envelope (responses/200/data)
 */
export const CostSchema = z.object({
  type: z.enum(['credit', 'lite']).optional(),
  value: z.number().optional(),
});

export const EnvelopeLinksSchema = z.object({
  self: z.string().optional(),
});

export const KyckrOrderResponseSchema = z.looseObject({
  orderId: z.union([z.number(), z.string()]).optional(), // schema text says number; real sample shows string
  correlationId: z.string().optional(),
  customerReference: z.string().optional(),
  timeStamp: z.string().optional(), // date-time-ish
  details: z.string().optional(),

  cost: CostSchema.optional(),
  links: EnvelopeLinksSchema.optional(),

  data: KyckrCompanyProfileSchema.optional(),
});

export type KyckrOrderResponse = z.infer<typeof KyckrOrderResponseSchema>;
